/**
 * EntrySubmissionView.jsx
 * Presentational, read-only rendering of a single entry submission's full
 * fields — rider/passenger/entrant/machines/payment/declaration — reusing
 * the live entry form's own CSS classes (EntryForm.css) so it looks exactly
 * like the on-screen form the entrant filled in and signed. Deliberately
 * has no modal/overlay/portal chrome of its own and never touches
 * @media print styling — callers decide how to present it:
 *   - SubmissionPrintView.jsx wraps it in the secretary's modal shell for a
 *     quick in-app look.
 *   - EntryProofPage.jsx renders it as a plain full page, which is what
 *     Playwright captures (via pdf_utils.py) into the PDF attached to the
 *     entrant's confirmation email and downloadable by the secretary —
 *     forcing "screen" media emulation there is what keeps the boxes/
 *     shadows intact instead of the flattened print-CSS look.
 */
const CLASS_FLAGS = [
  { key: 'openCapacity', label: 'Open Capacity' },
  { key: 'forgottenEra', label: 'Forgotten Era' },
  { key: 'roadLegal',    label: 'Road Legal' },
  { key: 'vintage',      label: 'Vintage' },
  { key: 'classic',      label: 'Classic' },
];

const VEHICLE_TYPE_LABELS = {
  Motorcycle: 'Motorcycle', Sidecar: 'Sidecar', Trike: 'Trike',
  Morgan1P: 'Morgan (ballast)', Morgan2P: 'Morgan (passenger)',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

function machineLabel(m, i) {
  const bits = [`Machine ${i + 1}`];
  const classes = CLASS_FLAGS.filter(f => m.classFlags?.[f.key]).map(f => f.label);
  if (classes.length) bits.push(classes.join(', '));
  if (m.ridingNo) bits.push(`No. ${m.ridingNo}`);
  return bits.join(' — ');
}

function parseDeclaration(text) {
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed?.declarations)) return parsed;
  } catch {
    // fall through
  }
  return null;
}

function ReadOnlyField({ label, value }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <input type="text" value={value ?? ''} readOnly />
    </div>
  );
}

export default function EntrySubmissionView({ event, submission }) {
  const payload = submission.payload || {};
  const rider = payload.rider || {};
  const passenger = payload.passenger || {};
  const hasPassenger = !!payload.hasPassenger;
  const entrant = payload.entrant || {};
  const entrantDiffers = !!payload.entrantDiffers;
  const machines = payload.machines || [];
  const declaration = parseDeclaration(submission.declaration_text);
  const hasParentGuardian = !!submission.signature_parent_guardian_name;

  return (
    <main className="secretary-main">
      <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
        <div className="sec-section-header">
          <div>
            <h2>🏁 {event.eventname || event.hill}</h2>
            <p style={{ color: 'var(--text-light)', margin: '0.25rem 0 0', fontSize: '1rem' }}>{fmtDate(event.date)}</p>
            <p className="event-meta-row" style={{ color: 'var(--text-light)', margin: '0.4rem 0 0', fontSize: '0.85rem' }}>
              <span><strong>Event:</strong> Hillclimb</span>
              <span><strong>Venue:</strong> {event.hill}</span>
              <span><strong>Permit No:</strong> {event.acu_permit || '—'}</span>
              <span><strong>Course Lic/Cert No:</strong> {event.course_licence_no || '—'}</span>
            </p>
          </div>
        </div>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>
          Organisers: NHCA. This event is held under the National Sporting Code of the Auto-Cycle Union, the
          Standing Regulations, Supplementary Regulations and any Final Instructions issued for the meeting.
        </p>
        <p className="no-print" style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
          Submission #{submission.id} — status: <strong>{submission.status}</strong> — submitted {submission.submitted_at ? new Date(submission.submitted_at).toLocaleString('en-GB') : '—'}
        </p>
      </div>

      {/* Rider */}
      <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
        <div className="form-section-label">Rider</div>
        <div className="form-grid">
          <div className="form-row">
            <ReadOnlyField label="First name(s)" value={rider.firstName} />
            <ReadOnlyField label="Surname" value={rider.surname} />
          </div>
          <div className="form-row">
            <ReadOnlyField label="Email address" value={rider.email} />
            <ReadOnlyField label="Telephone" value={rider.tel} />
          </div>
          <div className="form-row">
            <ReadOnlyField label="Address" value={rider.address} />
            <ReadOnlyField label="Postcode" value={rider.postcode} />
          </div>
          <div className="form-row">
            <ReadOnlyField label="Date of birth" value={rider.dob} />
            <ReadOnlyField label="ACU Licence / Registration No." value={rider.acuLicence} />
          </div>
          <div className="form-row">
            <ReadOnlyField label="ACU Affiliated Club" value={rider.acuClub} />
            <ReadOnlyField label="Rider's emergency contact (name & number)" value={payload.riderEmergencyContact} />
          </div>
        </div>
      </div>

      {/* Passenger */}
      <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
        <div className="form-section-label">Passenger</div>
        {hasPassenger ? (
          <div className="form-grid">
            <div className="form-row">
              <ReadOnlyField label="First name(s)" value={passenger.firstName} />
              <ReadOnlyField label="Surname" value={passenger.surname} />
            </div>
            <div className="form-row">
              <ReadOnlyField label="Address" value={passenger.address} />
              <ReadOnlyField label="Postcode" value={passenger.postcode} />
            </div>
            <div className="form-row">
              <ReadOnlyField label="Date of birth" value={passenger.dob} />
              <ReadOnlyField label="Telephone" value={passenger.tel} />
            </div>
            <div className="form-row">
              <ReadOnlyField label="ACU Licence / Registration No." value={passenger.acuLicence} />
              <ReadOnlyField label="ACU Affiliated Club" value={passenger.acuClub} />
            </div>
            <div className="form-row">
              <ReadOnlyField label="Passenger's emergency contact (name & number)" value={payload.passengerEmergencyContact} />
            </div>
          </div>
        ) : (
          <p className="muted-note">No passenger on this entry.</p>
        )}
      </div>

      {/* Entrant if different */}
      <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
        <div className="form-section-label">Entrant</div>
        {entrantDiffers ? (
          <div className="form-grid">
            <div className="form-row">
              <ReadOnlyField label="Company / Name" value={entrant.name} />
              <ReadOnlyField label="Entrant's Licence No." value={entrant.licenceNo} />
            </div>
            <ReadOnlyField label="Address" value={entrant.address} />
          </div>
        ) : (
          <p className="muted-note">Entrant is the rider.</p>
        )}
      </div>

      {/* Machines */}
      <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
        <div className="form-section-label">Machine entries</div>
        {machines.map((m, i) => (
          <div key={i} className="machine-block">
            <div className="machine-block-header">
              <span className="form-section-label" style={{ margin: 0 }}>{machineLabel(m, i)}</span>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Class entered</label>
                <div className="class-flags-choice">
                  {CLASS_FLAGS.map(f => (
                    <label key={f.key} className="inline-checkbox">
                      <input type="checkbox" checked={!!m.classFlags?.[f.key]} readOnly />
                      {f.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-row-3">
                <ReadOnlyField label="Riding No. preferred" value={m.ridingNo} />
                <ReadOnlyField label="Machine model" value={m.model} />
                <ReadOnlyField label="Vehicle type" value={VEHICLE_TYPE_LABELS[m.vehicleType] || m.vehicleType} />
              </div>
              <div className="form-row-3">
                <ReadOnlyField label="Make" value={m.make} />
                <ReadOnlyField label="Capacity (cc)" value={m.capacity} />
                <ReadOnlyField label="Stroke (mm)" value={m.stroke} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Payment */}
      <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
        <div className="form-section-label">Payment</div>
        <div className="form-grid">
          <div className="form-group">
            <label>Payment method</label>
            <div className="payment-method-choice">
              {['bacs', 'cheque', 'covered'].map(mth => (
                <label key={mth} className="inline-checkbox">
                  <input type="radio" checked={submission.payment_method === mth} readOnly />
                  {mth === 'bacs' ? 'Bank transfer (BACS)' : mth === 'cheque' ? 'Cheque (post with printed form)' : 'I have a code'}
                </label>
              ))}
            </div>
          </div>
          <div className="form-row-4">
            {submission.payment_method !== 'covered' && (
              <div className="form-group">
                <label>AMOUNT ({submission.payment_method === 'cheque' ? 'Cheque' : 'Bank Transfer'})</label>
                <div className="currency-input-wrap">
                  <span className="currency-prefix">£</span>
                  <input type="text" value={submission.payment_amount != null ? Number(submission.payment_amount).toFixed(2) : ''} readOnly />
                </div>
              </div>
            )}
            {submission.payment_method === 'bacs' && (
              <ReadOnlyField label="Transfer reference" value={submission.payment_bank_reference} />
            )}
          </div>
        </div>
        {submission.licence_option && (
          <p className="payment-info-note" style={{ marginTop: '0.75rem' }}>
            {submission.licence_option === 'giag'
              ? <>Give It A Go <strong>£{Number(submission.licence_fee).toFixed(2)}</strong>, all-in: a day licence, day NHCA
                  membership and this machine&apos;s entry.
                  {payload.licence?.firstTimer && ' The entrant confirmed they have not competed in a hill climb before.'}</>
              : <>Day licence <strong>£{Number(submission.licence_fee).toFixed(2)}</strong>, added to the entry fees
                  (included in the amount above).</>}
          </p>
        )}
        {submission.day_membership && (
          <p className="payment-info-note" style={{ marginTop: '0.75rem' }}>
            Day NHCA membership <strong>£{Number(submission.membership_fee).toFixed(2)}</strong>, added to the entry fees
            (included in the amount above).
          </p>
        )}
        {submission.payment_covers_others && submission.coverage_code && (
          <p className="payment-info-note" style={{ marginTop: '0.75rem' }}>
            This payment covers other entrants — coverage code <strong>{submission.coverage_code}</strong>.
          </p>
        )}
        {submission.covered_by_submission_id && (
          <p className="payment-info-note" style={{ marginTop: '0.75rem' }}>
            This entry&apos;s fee was covered by submission #{submission.covered_by_submission_id}&apos;s bank transfer — no payment made directly.
          </p>
        )}
      </div>

      {/* Note to secretary */}
      {payload.secretaryNote && (
        <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
          <div className="form-section-label">Note to secretary</div>
          <p>{payload.secretaryNote}</p>
        </div>
      )}

      {/* Declaration & signature */}
      <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
        <div className="form-section-label">Declaration</div>
        {declaration ? (
          <>
            <ul className="declaration-list">
              {declaration.declarations.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
            <p className="declaration-risk"><strong>Acknowledgement of the risks of motorsport:</strong> {declaration.riskAcknowledgement}</p>
          </>
        ) : (
          <p className="muted-note">Declaration text (version {submission.declaration_version || '—'}) not available in a readable form.</p>
        )}

        <div className="form-grid" style={{ marginTop: '1rem' }}>
          <div className="form-row">
            <ReadOnlyField label="Rider's typed signature (full name)" value={submission.signature_rider_name} />
            <ReadOnlyField label="Date" value={submission.signature_rider_date} />
          </div>
          {hasPassenger && (
            <div className="form-row">
              <ReadOnlyField label="Passenger's typed signature (full name)" value={submission.signature_passenger_name} />
              <ReadOnlyField label="Date" value={submission.signature_passenger_date} />
            </div>
          )}
          {hasParentGuardian && (
            <div className="form-row">
              <ReadOnlyField label="Parent/guardian typed signature" value={submission.signature_parent_guardian_name} />
              <ReadOnlyField label="Date" value={submission.signature_parent_guardian_date} />
            </div>
          )}
          <label className="inline-checkbox agree-checkbox">
            <input type="checkbox" checked={!!submission.signature_agreed} readOnly />
            {hasPassenger || hasParentGuardian ? 'We have read and agree to the declarations above.' : 'I have read and agree to the declarations above.'}
          </label>
        </div>
      </div>
    </main>
  );
}
